import React from 'react';
import { motion } from 'motion/react';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { Sparkles, TrendingUp, Users, Play, ArrowRight, BookOpen, Cpu, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';

const CASE_STUDIES = [
  {
    name: "AeroPhysics Digital",
    result: "1.2 Billion Views",
    platform: "TikTok & YouTube Shorts",
    quote: "Fun Circle Games allowed us to scale from 2 videos a week to 3 videos a day. The retention on the ball simulations is insane.",
    background: "bg-cyan-600"
  },
  {
    name: "ZenLoops ASMR",
    result: "500K Subscribers",
    platform: "YouTube",
    quote: "The audio sync precision is what sets this apart. We've built an entire relaxation channel using just the Rhythmic Mode.",
    background: "bg-zinc-900"
  }
];

const CaseStudies: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Creator Success Stories | Case Studies"
        description="See how top creators are using Fun Circle Games to generate billions of views. real results, real metrics, and real growth strategies."
        keywords="physics video success, creator case studies, viral growth strategy, youtube shorts success, tiktok physics channels"
        canonical="/case-studies"
      />
      <BackgroundAnimation />

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
             <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-8 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>Creator Success</span>
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter leading-none">
              Creator <span className="text-zinc-400">Success</span>
            </h1>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-sans">
              Discover how professional content studios and independent creators are building empires using our physics engine.
            </p>
          </motion.div>

          <div className="space-y-12 mb-20">
            {CASE_STUDIES.map((study, idx) => (
              <motion.div
                key={study.name}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.2 }}
                className="grid grid-cols-1 lg:grid-cols-2 rounded-[3rem] overflow-hidden border border-zinc-950/10 shadow-xl bg-white"
              >
                <div className={cn("aspect-video lg:aspect-auto flex items-center justify-center relative", study.background)}>
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="relative z-10 text-center p-12">
                       <Play className="w-20 h-20 text-white opacity-40 mx-auto mb-6" />
                       <div className="inline-flex items-center gap-4 text-white/80 font-bold uppercase tracking-[0.2em] text-xs">
                          <Users className="w-4 h-4" />
                          {study.platform}
                       </div>
                    </div>
                </div>
                <div className="p-12 md:p-20 flex flex-col justify-center">
                  <h2 className="text-4xl md:text-6xl font-bold font-display uppercase tracking-tight text-zinc-950 mb-4">
                    {study.result}
                  </h2>
                  <p className="text-zinc-400 font-bold uppercase tracking-widest text-xs mb-10">Total Reach by {study.name}</p>
                  <blockquote className="relative">
                    <span className="absolute -left-8 -top-8 text-8xl text-zinc-50 font-serif opacity-50">"</span>
                    <p className="text-xl text-zinc-600 italic leading-relaxed relative z-10">
                      {study.quote}
                    </p>
                  </blockquote>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="p-12 md:p-20 rounded-[3rem] bg-cyan-600 text-white text-center">
             <h2 className="text-3xl md:text-5xl font-bold font-display uppercase tracking-tighter mb-8">
               Become Our Next Success Story
             </h2>
             <Link to="/register" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-white text-black hover:bg-zinc-200 transition-all font-bold uppercase tracking-[0.3em] text-xs font-sans")}>
               Start Your Free Channel <ArrowRight className="ml-3 w-5 h-5" />
             </Link>
          </div>

          <div className="pt-24 border-t border-zinc-100 text-left space-y-6 max-w-4xl mx-auto">
            <h3 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-950">Build Similar Results</h3>
            <p className="text-zinc-500 text-sm font-medium font-sans">
              Learn the strategies, unlock the pro tools, and start scaling your channel today:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/plans" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all">
                <div className="space-y-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <h4 className="text-xs font-bold text-zinc-900 uppercase">Pricing Plans</h4>
                  <p className="text-[10px] text-muted-foreground">Get commercial license & priority rendering.</p>
                </div>
                <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  Compare <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              <Link to="/tutorials" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all">
                <div className="space-y-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <h4 className="text-xs font-bold text-zinc-900 uppercase">Tutorials</h4>
                  <p className="text-[10px] text-muted-foreground">Learn sound design & visual setup keys.</p>
                </div>
                <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  Learn <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              <Link to="/ball-simulation" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all">
                <div className="space-y-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <Cpu className="w-4 h-4" />
                  </div>
                  <h4 className="text-xs font-bold text-zinc-900 uppercase">Engine Sandbox</h4>
                  <p className="text-[10px] text-muted-foreground">Experiment in real-time in the browser.</p>
                </div>
                <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  Sandbox <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              <Link to="/showcase" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all">
                <div className="space-y-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <Video className="w-4 h-4" />
                  </div>
                  <h4 className="text-xs font-bold text-zinc-900 uppercase">Showcase</h4>
                  <p className="text-[10px] text-muted-foreground">Remix viral simulations from other artists.</p>
                </div>
                <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                  Remix <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <DiscordCTA variant="banner" />

      <InternalLinksSection />
    </div>
  );
};

export default CaseStudies;
